import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getAuth,
  onAuthStateChanged,
  signInAnonymously
} from 'firebase/auth';
import {
  getDatabase,
  ref,
  get,
  set,
  push,
  onValue,
  runTransaction
} from 'firebase/database';
import {
  getFunctions,
  httpsCallable
} from 'firebase/functions';
import { getCachedModelos, getCachedLicenseTokens } from '../services/rtdbCache';
import './IAVortex.css';

// Iconos usando emojis y símbolos Unicode
const Icons = {
  robot: '🤖',
  user: '👤',
  book: '📚',
  lightbulb: '💡',
  info: 'ℹ️',
  mic: '🎤',
  micOff: '🔇',
  send: '📤',
  refresh: '🔄',
  settings: '⚙️',
  help: '❓',
  warning: '⚠️',
  check: '✅',
  close: '✕',
  image: '🖼️',
  attach: '📎'
};

// Modelos de IA disponibles
const DEFAULT_MODELS = {
  deepseek: {
    displayName: 'DEEPSEEK AI (Gratis)',
    modelName: 'deepseek-chat',
    apiKey: ''
  },
  llama3: {
    displayName: 'LLAMA 3 AI (Gratis)',
    modelName: 'llama-3-8b-instruct',
    apiKey: ''
  },
  qwen: {
    displayName: 'QWEN AI (Gratis)',
    modelName: 'qwen2.5-7b-instruct',
    apiKey: ''
  },
  gpt35: {
    displayName: 'GPT-3.5 (Rápido)',
    modelName: 'gpt-3.5-turbo',
    apiKey: ''
  },
  gemma: {
    displayName: 'Gemma 2B (Ultra Rápido)',
    modelName: 'gemma-2b-it',
    apiKey: ''
  },
  premium: {
    displayName: 'Vortex Premium (Vision)',
    modelName: 'gemini-2.5-flash-lite',
    apiKey: ''
  }
};

// Estructura de mensaje del chat
const createMessage = (text, source, isUser = false, image = null) => ({
  id: Date.now() + Math.random(),
  text,
  source,
  isUser,
  image, // Base64 del la imagen si existe
  timestamp: new Date().toISOString()
});

// Función para detectar si es pregunta académica EXANI (acepta cualquier consulta)
const esPreguntaMaritima = (consulta) => {
  // Acepta todas las consultas académicas relacionadas con el EXANI-II
  return consulta.trim().length > 0;
};

// Función para extraer término principal
const extraerTerminoPrincipal = (consulta) => {
  const consultaNorm = consulta.toLowerCase().trim();
  const articuloRegex = /articulo\s+(\d{1,3}|[ivxlc]+|primero|segundo|tercero|cuarto|quinto|sexto|septimo|octavo|noveno|decimo|\d{1,3}º)/i;
  const match = articuloRegex.exec(consultaNorm);

  if (match) {
    return match[0].trim();
  }

  const palabrasPregunta = [
    'que', 'quees', 'queson', 'ques', 'cual', 'cuales', 'como', 'donde',
    'cuando', 'porque', 'para', 'del', 'de', 'la', 'las', 'el', 'los',
    'una', 'unas', 'un', 'unos', 'en', 'al', 'ambito', 'maritimo', 'marina',
    'contexto', 'definicion', 'significado', 'campo', 'area', 'sector'
  ];

  const palabras = consultaNorm.split(' ').filter(
    palabra => palabra.length > 2 && !palabrasPregunta.includes(palabra)
  );

  return palabras[palabras.length - 1] || consultaNorm;
};

// Simulación de búsqueda en repositorios
const buscarEnRepositorios = async (consulta) => {
  const termino = extraerTerminoPrincipal(consulta);

  // Simulación de búsqueda en glosario
  const glosario = {
    'loam': 'Ley Orgánica de la Armada de México - Norma fundamental que regula la organización y funcionamiento de la Armada de México.',
    'constitucion': 'Carta Magna de México - Documento fundamental que establece los derechos y obligaciones de los ciudadanos mexicanos.',
    'buque': 'Embarcación de gran tamaño destinada a la navegación marítima.',
    'marina': 'Institución armada encargada de la defensa marítima y naval de la nación.',
    'capitan': 'Grado militar de la Armada de México, superior a Teniente e inferior a Capitán de Fragata.'
  };

  const resultado = glosario[termino.toLowerCase()];
  if (resultado) {
    return `📚 **Información del Glosario:**\n\n${resultado}`;
  }

  return null;
};

const IAVortex = () => {
  const location = useLocation();
  // Estados principales
  const [messages, setMessages] = useState([
    createMessage(
      '¡Hola! Soy VORTEX EXANI IA, tu asistente virtual para consultas sobre todos los módulos de tu carrera, temas del EXANI-II y más. ¿En qué puedo ayudarte hoy?',
      'IA'
    )
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedModelKey, setSelectedModelKey] = useState('deepseek');
  const [openRouterModels, setOpenRouterModels] = useState(DEFAULT_MODELS);
  const [fontSize, setFontSize] = useState(16);
  const [chatBackground, setChatBackground] = useState('');
  const [tokensLeft, setTokensLeft] = useState(2000000);
  const [tokensUsed, setTokensUsed] = useState(0);
  const [isNearLimit, setIsNearLimit] = useState(false);
  const [daysLeft, setDaysLeft] = useState(30);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Referencias
  const messagesEndRef = useRef(null);
  const textAreaRef = useRef(null);
  const auth = getAuth();
  const database = getDatabase();

  // Scroll automático al final de los mensajes
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Cargar prompt inicial si viene de navegación
  useEffect(() => {
    if (location.state?.initialPrompt) {
      handleSendMessage(location.state.initialPrompt);
    }
  }, [location.state?.initialPrompt]);

  // Autenticación automática
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        signInAnonymously(auth).catch(console.error);
      }
    });

    return () => unsubscribe();
  }, [auth]);

  // Sincronizar tokens en tiempo real desde Realtime Database
  useEffect(() => {
    if (!currentUser || !currentUser.email) return;

    const licenseKey = currentUser.uid;
    const licenseRef = ref(database, `licencesPremium/${licenseKey}`);

    console.log(`[IAVortex] 🔄 Suscribiéndose a tokens de: ${licenseKey}`);

    // Cargar desde caché local primero como fallback
    const cachedLicense = getCachedLicenseTokens(licenseKey);
    if (cachedLicense) {
      const left = cachedLicense.tokensAvailable - cachedLicense.tokensUsed;
      const days = cachedLicense.expiresAt ? Math.max(0, Math.ceil((cachedLicense.expiresAt - Date.now()) / (24 * 60 * 60 * 1000))) : 30;
      setTokensLeft(left);
      setTokensUsed(cachedLicense.tokensUsed);
      setDaysLeft(days);
      setIsNearLimit(left < 100000);
      console.log(`[IAVortex] 📦 Tokens desde caché: ${left} restantes`);
    }

    const unsubscribe = onValue(licenseRef, (snapshot) => {
      if (snapshot.exists()) {
        const license = snapshot.val();
        const tokensAvailable = license.tokensAvailable || 2000000;
        const tokensUsedVal = license.tokensUsed || 0;
        const expiresAt = license.expiresAt || license.tokenPeriodEnd;

        const left = tokensAvailable - tokensUsedVal;
        const days = expiresAt ? Math.max(0, Math.ceil((expiresAt - Date.now()) / (24 * 60 * 60 * 1000))) : 30;

        setTokensLeft(left);
        setTokensUsed(tokensUsedVal);
        setDaysLeft(days);
        setIsNearLimit(left < 100000);
        console.log(`[IAVortex] 📊 Sincronizados tokens: ${left} restantes de ${tokensAvailable} (Usados: ${tokensUsedVal})`);
      } else {
        console.warn(`[IAVortex] ⚠️ No se encontró licencia premium para: ${licenseKey}. Usando valores por defecto.`);
        setTokensLeft(2000000);
        setTokensUsed(0);
        setDaysLeft(30);
        setIsNearLimit(false);
      }
    });

    return () => unsubscribe();
  }, [currentUser, database]);

  // Cargar modelos desde Firebase (con caché)
  useEffect(() => {
    const loadModels = async () => {
      try {
        const models = await getCachedModelos();

        if (Object.keys(models).length > 0) {
          setOpenRouterModels(models);
          setSelectedModelKey(Object.keys(models)[0]);
          console.log('[IAVortex] 📦 Modelos cargados desde caché');
        } else {
          setOpenRouterModels(DEFAULT_MODELS);
          setSelectedModelKey('deepseek');
        }
      } catch (error) {
        console.error('Error cargando modelos:', error);
        setOpenRouterModels(DEFAULT_MODELS);
        setSelectedModelKey('deepseek');
      }
    };

    loadModels();
  }, [database]);

  // Manejar selección de imagen
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 4 * 1024 * 1024) {
        setErrorMessage('La imagen es demasiado grande (máx 4MB)');
        setShowErrorDialog(true);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result); // Base64
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearSelectedImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Función para enviar mensaje
  const handleSendMessage = useCallback(async (userMessage) => {
    if (!userMessage.trim() || isLoading) return;

    // Agregar mensaje del usuario
    const userMsg = createMessage(userMessage, 'Usuario', true, selectedImage);
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setSelectedImage(null);
    setImagePreview(null);
    setIsLoading(true);
    setLoadingStatus('🔄 Procesando consulta...');

    try {
      // Mensaje de conexión
      const connectingMsg = createMessage('🔄 Conectando con la IA...', 'IA');
      setMessages(prev => [...prev, connectingMsg]);

      // Simulación de llamada a IA
      setLoadingStatus('🤖 Conectando con la IA...');

      // Simular delay de respuesta
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Respuesta simulada de la IA
      const aiResponse = await generateAIResponse(userMessage, userMsg.image);

      // Remover mensaje de conexión y agregar respuesta
      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.text.includes('🔄 Conectando'));
        return [...filtered, createMessage(aiResponse, 'IA')];
      });

      // Búsqueda en repositorios en segundo plano
      setTimeout(async () => {
        const repoInfo = await buscarEnRepositorios(userMessage);
        if (repoInfo) {
          setMessages(prev => [...prev, createMessage(repoInfo, 'Repositorio')]);
        }
      }, 1000);

    } catch (error) {
      console.error('Error en IA:', error);
      setErrorMessage('Error al conectar con la IA. Intenta de nuevo.');
      setShowErrorDialog(true);
    } finally {
      setIsLoading(false);
      setLoadingStatus('');
    }
  }, [isLoading]);

  // Función para generar respuesta de IA (Conexión Real con OpenRouter)
  const generateAIResponse = async (userMessage, base64Image) => {
    const currentModel = openRouterModels[selectedModelKey];

    if (!currentModel) {
      return "⚠️ Configuración de API no encontrada para este modelo. Por favor, contacta al administrador.";
    }

    const isVertexAI = currentModel.modelName === 'vertex_ai' || 
                       currentModel.modelName === 'gemini-2.5-flash-lite';

    // Verificar límite de tokens si es modelo de pago
    if (isVertexAI && tokensLeft <= 0) {
      return "⚠️ Has agotado tus tokens mensuales. Para adquirir nuevos tokens inmediatamente, es necesario renovar tu suscripción.";
    }

    const systemPrompt = `Eres VORTEX IA, un asistente experto en conocimientos marineros, legislación naval y procedimientos de la Armada de México. 
    Tu objetivo es ayudar al personal a prepararse para exámenes de ascenso. 
    Siempre sé profesional, técnico y preciso. 
    Si la consulta no es marítima, responde amablemente pero intenta relacionarla con el ámbito naval si es posible.`;

    if (isVertexAI) {
      console.log('💎 [Premium] Proxying IA por backend (gemini-2.5-flash-lite)');
      try {
        const modelName = 'gemini-2.5-flash-lite';

        let imageData = null;
        let imageMimeType = null;
        if (base64Image) {
          imageMimeType = base64Image.substring(base64Image.indexOf(":") + 1, base64Image.indexOf(";"));
          imageData = base64Image.substring(base64Image.indexOf(",") + 1);
        }

        const proxyFn = httpsCallable(getFunctions(), 'vortexGenerate');
        const result = await proxyFn({
          model: modelName,
          prompt: userMessage || "",
          systemInstruction: systemPrompt,
          type: 'chat',
          imageData,
          imageMimeType
        });

        const aiText = result.data?.text;
        if (!aiText) throw new Error('Respuesta vacía de Gemini API');

        // Estimar tokens usados para Vertex AI (aprox 1 token = 4 caracteres)
        const totalChars = (userMessage || '').length + (aiText || '').length;
        const estimatedTokens = Math.ceil(totalChars / 4) + (base64Image ? 258 : 0);

        if (auth.currentUser && auth.currentUser.email) {
          try {
            const tokenRef = ref(database, `licencesPremium/${auth.currentUser.uid}/tokensUsed`);
            await runTransaction(tokenRef, (current) => (current || 0) + estimatedTokens);
            console.log(`[IAVortex] 💳 Tokens deducidos (Vertex AI): ${estimatedTokens} en RTDB`);
          } catch (tokenErr) {
            console.warn('[IAVortex] ⚠️ Error deduciendo tokens en RTDB:', tokenErr);
          }
        }

        return aiText;
      } catch (error) {
        console.error("Vertex AI Error:", error);
        throw error;
      }
    }

    // Caso OpenRouter
    try {
      let imageData = null;
      let imageMimeType = null;
      if (base64Image) {
        imageMimeType = base64Image.substring(base64Image.indexOf(":") + 1, base64Image.indexOf(";"));
        imageData = base64Image.substring(base64Image.indexOf(",") + 1);
      }

      const proxyFn = httpsCallable(getFunctions(), 'vortexGenerate');
      const result = await proxyFn({
        model: selectedModelKey,
        prompt: userMessage || "",
        systemInstruction: systemPrompt,
        type: 'chat',
        imageData,
        imageMimeType
      });

      const aiText = result.data?.text;
      if (!aiText) throw new Error('Respuesta vacía de la API');

      // Actualizar tokens usados en Realtime Database para OpenRouter
      if (result.data?.usage && auth.currentUser && auth.currentUser.email) {
        try {
          const usedTokens = result.data.usage.total_tokens;
          const tokenRef = ref(database, `licencesPremium/${auth.currentUser.uid}/tokensUsed`);

          await runTransaction(tokenRef, (current) => {
            return (current || 0) + usedTokens;
          });
          console.log(`[IAVortex] 💳 Tokens actualizados en RTDB (OpenRouter): +${usedTokens} para ${auth.currentUser.uid}`);
        } catch (tokenErr) {
          console.warn('[IAVortex] ⚠️ Error actualizando tokens en RTDB:', tokenErr);
        }
      }

      return aiText;

    } catch (error) {
      console.error("IA Error:", error);
      throw error;
    }
  };

  // Función para manejar entrada de voz
  const handleVoiceInput = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.lang = 'es-ES';
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onstart = () => {
        setIsListening(true);
        setLoadingStatus('🎤 Escuchando...');
      };

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputText(transcript);
        setIsListening(false);
        setLoadingStatus('');
        // Autocomand: Enviar automáticamente tras reconocimiento
        if (transcript.trim()) {
          handleSendMessage(transcript);
        }
      };

      recognition.onerror = (event) => {
        console.error('Error de reconocimiento de voz:', event.error);
        setIsListening(false);
        setLoadingStatus('');
        setErrorMessage('Error en el reconocimiento de voz. Intenta de nuevo.');
        setShowErrorDialog(true);
      };

      recognition.onend = () => {
        setIsListening(false);
        setLoadingStatus('');
      };

      recognition.start();
    } else {
      setErrorMessage('El reconocimiento de voz no está disponible en tu navegador.');
      setShowErrorDialog(true);
    }
  };

  // Función para nuevo chat
  const handleNewChat = () => {
    setMessages([
      createMessage(
        '¡Hola! Soy VORTEX IA, tu asistente virtual para consultas sobre conocimientos marineros, legislación naval y más. ¿En qué puedo ayudarte hoy?',
        'IA'
      )
    ]);
    setInputText('');
  };

  // Función para recargar modelos
  const handleReloadModels = async () => {
    try {
      const modelsRef = ref(database, 'config/apis/modelos');
      const snapshot = await get(modelsRef);

      if (snapshot.exists()) {
        const models = {};
        snapshot.forEach((child) => {
          const key = child.key;
          const value = child.val();

          if (typeof value === 'object' && value !== null) {
            models[key] = {
              displayName: value.displayName || key,
              modelName: value.modelName || key,
              apiKey: value.apiKey || ''
            };
          } else {
            models[key] = {
              displayName: `${key} (${value})`,
              modelName: value?.toString() || key,
              apiKey: ''
            };
          }
        });

        setOpenRouterModels(models);
        if (Object.keys(models).length > 0) {
          setSelectedModelKey(Object.keys(models)[0]);
        }
        console.log('[IAVortex] 🔄 Modelos recargados desde RTDB');
      }
    } catch (error) {
      console.error('Error recargando modelos:', error);
      setErrorMessage('Error al recargar modelos desde Firebase.');
      setShowErrorDialog(true);
    }
  };

  // Función para cambiar fondo
  const handleBackgroundChange = (background) => {
    setChatBackground(background);
    // Aquí podrías guardar en localStorage o Firebase
    localStorage.setItem('chatBackground', background);
  };

  // Función para cambiar tamaño de fuente
  const handleFontSizeChange = (size) => {
    setFontSize(size);
    localStorage.setItem('fontSize', size.toString());
  };

  // Función para enviar con Enter
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputText.trim() && !isLoading) {
        handleSendMessage(inputText);
      }
    }
  };

  // Auto-resize del textarea
  const handleTextareaInput = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  // Compute background class from selected theme
  const bgClass = chatBackground && !chatBackground.startsWith('#')
    ? `bg-${chatBackground}`
    : '';
  const containerStyle = chatBackground.startsWith('#')
    ? { background: chatBackground }
    : {};

  return (
    <div
      className={`ia-vortex-container${bgClass ? ` ${bgClass}` : ''}`}
      style={containerStyle}
    >
      {/* Header Táctico */}
      <div className="ia-vortex-header">
        <div className="ia-vortex-top-bar">
          <div className="ia-vortex-title-group">
            <h1 className="ia-vortex-title">
              <span>⚡</span> VORTEX EXANI IA
            </h1>
            <span className="ia-vortex-badge">ASISTENTE ACADÉMICO</span>
          </div>

          {/* Botones de acción tácticos */}
          <div className="ia-vortex-actions">
            <button
              className="ia-vortex-action-button"
              onClick={handleReloadModels}
              title="Recargar modelos"
            >
              {Icons.refresh}
            </button>
            <button
              className="ia-vortex-action-button"
              onClick={handleNewChat}
              title="Nuevo chat"
            >
              {Icons.book}
            </button>
            <button
              className="ia-vortex-action-button"
              onClick={() => setShowConfigDialog(true)}
              title="Configuración"
            >
              {Icons.settings}
            </button>
            <button
              className="ia-vortex-action-button"
              onClick={() => setShowHelpDialog(true)}
              title="Ayuda"
            >
              {Icons.help}
            </button>
            <button
              className="ia-vortex-action-button"
              onClick={() => setShowErrorDialog(true)}
              title="Diagnóstico"
            >
              {Icons.warning}
            </button>
          </div>
        </div>

        {/* Selector de modelo */}
        <div className="ia-vortex-model-selector">
          {Object.entries(openRouterModels).map(([key, model]) => (
            <button
              key={key}
              className={`ia-vortex-model-button ${selectedModelKey === key ? 'active' : ''}`}
              onClick={() => setSelectedModelKey(key)}
            >
              {model.displayName}
            </button>
          ))}
        </div>

        {/* Información de recursos / tokens */}
        <div className="ia-vortex-token-info">
          <span>TOKENS: <span className="ia-vortex-token-count">{tokensLeft.toLocaleString()}</span></span>
          <span>PERIODO: <span className="ia-vortex-token-count">{daysLeft} DÍAS</span></span>
          {isNearLimit && <span className="ia-vortex-token-warning">⚠️ LÍMITE PRÓXIMO</span>}
        </div>
      </div>

      {/* Contenido principal */}
      <div className="ia-vortex-content">
        {/* Lista de mensajes */}
        <div className="ia-vortex-messages">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`ia-vortex-message ${message.isUser ? 'user' : message.source.toLowerCase()}`}
            >
              <div className="ia-vortex-message-header">
                <span className="ia-vortex-message-icon">
                  {message.source === 'IA' ? '🤖' :
                    message.source === 'Usuario' ? '⚓' :
                      message.source === 'Repositorio' ? '📚' :
                        message.source === 'Sugerencia' ? '💡' :
                          'ℹ️'}
                </span>
                <span>{message.source === 'IA' ? 'VORTEX IA' : message.source}</span>
              </div>
              {message.image && (
                <div className="ia-vortex-message-image">
                  <img src={message.image} alt="Adjunto" />
                </div>
              )}
              <div
                className="ia-vortex-message-text"
                style={{ fontSize: `${fontSize}px` }}
              >
                {message.text}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Indicador de carga */}
        {isLoading && loadingStatus && (
          <div className="ia-vortex-loading">
            <div className="ia-vortex-loading-spinner"></div>
            <span>{loadingStatus}</span>
          </div>
        )}

        

        {/* Área de entrada */}
        <div className="ia-vortex-input-area">
          {imagePreview && (
            <div className="ia-vortex-image-preview-container">
              <img src={imagePreview} alt="Previsualización" />
              <button className="ia-vortex-clear-image" onClick={clearSelectedImage}>
                {Icons.close}
              </button>
            </div>
          )}
          <div className="ia-vortex-input-container">
            <input
              type="file"
              id="ia-vision-upload"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageSelect}
            />
            <button
              className="ia-vortex-attach-button"
              onClick={() => document.getElementById('ia-vision-upload').click()}
              title="Adjuntar imagen para análisis táctico (Vision)"
            >
              📷
            </button>
            <textarea
              ref={textAreaRef}
              className="ia-vortex-text-input"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              onInput={handleTextareaInput}
              placeholder="Consulta a VORTEX IA sobre tus modulos y dudas generales que tengas..."
              disabled={isLoading}
              rows={1}
            />

            <button
              className={`ia-vortex-voice-button ${isListening ? 'recording' : ''}`}
              onClick={handleVoiceInput}
              disabled={isLoading}
              title="Dictado por voz"
            >
              {isListening ? '🔇' : '🎤'}
            </button>

            <button
              className="ia-vortex-send-button"
              onClick={() => handleSendMessage(inputText)}
              disabled={!inputText.trim() || isLoading}
              title="Enviar comando"
            >
              <span>ENVIAR</span> 📤
            </button>
          </div>
        </div>
      </div>

      {/* Diálogo de ayuda */}
      {showHelpDialog && (
        <>
          <div className="ia-vortex-overlay" onClick={() => setShowHelpDialog(false)} />
          <div className="ia-vortex-info-dialog">
            <button
              className="ia-vortex-dialog-close"
              onClick={() => setShowHelpDialog(false)}
            >
              {Icons.close}
            </button>
            <h2 className="ia-vortex-dialog-title">¿Cómo funciona el asistente?</h2>
            <div className="ia-vortex-dialog-content">
              <p>• Puedes preguntar por texto o voz sobre temas navales y generales.</p>
              <p>• Los tokens son tu saldo mensual para usar modelos de Inteligencia artificial.</p>
              <p>• Los modelos gratuitos pueden dar respuestas incompletas o de menor calidad.</p>
              <br />
              <p><strong>Ejemplos de preguntas navales:</strong></p>
              <p>- ¿Qué es la LOAM?</p>
              <p>- ¿Cuáles son los grados de la Armada?</p>
              <p>- Explica el artículo 32 de la Constitución.</p>
              <p>- ¿Qué funciones tiene una Varenga?</p>
              <p>- ¿Qué es la obra viva de un buque?</p>
            </div>
            <button
              className="ia-vortex-dialog-button"
              onClick={() => setShowHelpDialog(false)}
            >
              Cerrar
            </button>
          </div>
        </>
      )}

      {/* Diálogo de errores */}
      {showErrorDialog && (
        <>
          <div className="ia-vortex-overlay" onClick={() => setShowErrorDialog(false)} />
          <div className="ia-vortex-info-dialog">
            <button
              className="ia-vortex-dialog-close"
              onClick={() => setShowErrorDialog(false)}
            >
              {Icons.close}
            </button>
            <h2 className="ia-vortex-dialog-title">Errores y advertencias</h2>
            <div className="ia-vortex-dialog-content">
              {errorMessage && <p>{errorMessage}</p>}
              {tokensLeft === 0 && <p>No tienes tokens disponibles. Usa un modelo gratuito o espera al próximo ciclo.</p>}
              {openRouterModels[selectedModelKey]?.displayName?.toLowerCase().includes('gratis') &&
                <p>Estás usando un modelo gratuito. La calidad puede ser limitada.</p>
              }
            </div>
            <button
              className="ia-vortex-dialog-button"
              onClick={() => setShowErrorDialog(false)}
            >
              Cerrar
            </button>
          </div>
        </>
      )}

      {/* Diálogo de configuración */}
      {showConfigDialog && (
        <>
          <div className="ia-vortex-overlay" onClick={() => setShowConfigDialog(false)} />
          <div className="ia-vortex-info-dialog">
            <button
              className="ia-vortex-dialog-close"
              onClick={() => setShowConfigDialog(false)}
            >
              {Icons.close}
            </button>
            <h2 className="ia-vortex-dialog-title">Configuración</h2>
            <div className="ia-vortex-dialog-content">
              <h3>Fondo de chat</h3>
              <div className="ia-config-bg-picker">
                {['', '1', '2', '3', '4', '5'].map((bg) => (
                  <button
                    key={bg}
                    onClick={() => handleBackgroundChange(bg)}
                    className={`ia-config-bg-swatch ia-config-bg-swatch--${bg || 'default'}${chatBackground === bg ? ' active' : ''}`}
                  >
                    {bg === '' ? 'Default' : ''}
                  </button>
                ))}
              </div>

              <h3>Tamaño de texto</h3>
              <div className="ia-config-font-row">
                <span>Pequeño</span>
                <input
                  type="range"
                  min="12"
                  max="24"
                  value={fontSize}
                  onChange={(e) => handleFontSizeChange(parseInt(e.target.value))}
                />
                <span>Grande</span>
              </div>

              <div className="ia-config-font-preview" style={{ fontSize: `${fontSize}px` }}>
                Vista previa del texto con el tamaño seleccionado
              </div>
            </div>
            <button
              className="ia-vortex-dialog-button"
              onClick={() => setShowConfigDialog(false)}
            >
              Cerrar
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default IAVortex; 